import { ref, watch, watchEffect, computed } from "vue";
import Tdsps from "src/composables/Tdsps";
import PlanFilters from "src/composables/PlanFilters";
import UsageFilters from "src/composables/UsageFilters";
import SortOptions from "src/composables/SortOptions";
import Brands from "src/composables/Brands";
import Terms from "src/composables/Terms";
import Utilities from "src/composables/Utilities";
import { pricing_api } from "boot/axios";
import _debounce from 'lodash/debounce';
const query_string = require('query-string');

const plan_map = function (plan) {
  if (plan) {
    if (!!plan.product?.early_termination_fee_pro_rated) {
      const amount = plan.product?.early_termination_fee / plan.product?.term;
      plan.product.early_termination_fee_text = `$${ amount.toFixed(2) } per remaining contract month`
    }
    else {
      const amount = plan.product?.early_termination_fee;
      plan.product.early_termination_fee_text = `$${ amount.toFixed(2) }`
    }
    plan.product.term_string = plan.product.term > 1 ? `${ plan.product.term } Months` : 'Month to Month'
    for (const key of Object.keys(plan)) {

    }
    // plan[`display_pricing_${usage}`]?.avg_cents
  }
  return plan
}

const historical_usage_plan_map = function (plan) {
  plan = plan_map(plan);
  if (valid_historical_usage.value) {
    let total_cost = 0
    let total_usage = 0
    let high, low

    for (const usage of valid_historical_usage.value) {
      total_usage += parseInt(usage.usage);
      const total = plan[`display_pricing_${ usage.usage }`]?.total || 0;
      total_cost += total;
      if (!high || high.total < total) {
        high = {
          total,
          months: [usage.short_month]
        }
      }
      else if (high?.total === total) {
        high.months.push(usage.short_month)
      }

      if (!low || low.total > total) {
        low = {
          total,
          months: [usage.short_month]
        }
      }
      else if (low?.total === total) {
        low.months.push(usage.short_month)
      }
    }
    if (total_cost > 0) {
      plan.total_cost = total_cost
      plan.total_usage = total_usage
      plan.high = high
      plan.low = low

      plan.average_total = Math.round(plan.total_cost / valid_historical_usage.value.length * 100) / 100;
      plan.total_cost = Math.round(plan.total_cost * 100) / 100;
      plan.total_average = Math.round(plan.total_cost * 1000 / plan.total_usage) / 10;
    }
  }
  return plan;
};

const { selected_tdsp } = Tdsps();
const { filters, combined_filters } = PlanFilters();
const {
  usage_type,
  usage_selected,
  historical_usage,
  show_historical_usage,
  historical_usage_values,
  valid_historical_usage
} = UsageFilters();

const { sort_option } = SortOptions();

const featured_plans = ref([]);
const filtered_plans = ref([]);
const search_plans_loading = ref(false);
const total_plans = ref(null);

 function debounce(func, timeout = 500){
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => { func.apply(this, args); }, timeout);
  };
}

const search_plans = async function () {
  try {

    if (!total_plans.value) {
      await first_search();
      await search_featured_plans();
    }

    if (!usage_selected.value) {
      filtered_plans.value = [];
      return [];
    }

    if (selected_tdsp.value) {
      search_plans_loading.value = true;
      filtered_plans.value = [];
      const data = await plan_search({
        tdsp_duns: selected_tdsp.value.duns_number,
        ...filters.selected_brands.length && { brand_id: filters.selected_brands },
        ...filters.selected_terms.length && { term: filters.selected_terms },
        ...filters.only_100_percent_green.selected && { percent_green: 100 },
        ...filters.only_no_deposit.selected && { is_pre_pay: true },
        ...filters.only_time_of_use.selected && { is_time_of_use: true },
        ...!filters.include_auto_pay.selected && { requires_auto_pay: false },
        ...!show_historical_usage.value && usage_selected.value && { display_usage: usage_selected.value },
        ...show_historical_usage.value && { display_usage: historical_usage_values.value },
        current: true,
        ended: false,
        disabled: false
      });

      const current_terms = data.map(x => x.product.term).filter(distinct);
      available_terms.value.forEach(x => {
        if (current_terms.includes(x.value)) {
          x.disabled = false;
        }
        else {
          x.disabled = true;
        }
      })

      const current_brands = data.map(x => x.product.brand._id).filter(distinct);
      available_brands.value.forEach(x => {
        if (current_brands.includes(x._id)) {
          x.disabled = false;
        }
        else {
          x.disabled = true;
        }
      })

      const has_100_green = data.some(x => x.product.percent_green === 100);
      filters.only_100_percent_green.disabled = !has_100_green;

      const has_pre_pay = data.some(x => x.product.is_pre_pay);
      filters.only_no_deposit.disabled = !has_pre_pay;

      const has_tou = data.some(x => x.product.is_time_of_use);
      filters.only_time_of_use.disabled = !has_tou;

      if (show_historical_usage.value) {
        filtered_plans.value = data.map(historical_usage_plan_map)
      }
      else {
        filtered_plans.value = data.map(plan_map)
      }
      sort_plans()
      return data;
    }
  }
  catch (err) {
    console.error(err);
  }
  finally {
    search_plans_loading.value = false;
  }
}

const search_featured_plans =  async function () {
  try {
    featured_plans.value = [];
    const { data } = await pricing_api.get(`${ process.env.VUE_APP_PLANS_SERVICE_URL }/api/featured_plans`, {
      params: {
        tdsp_duns: selected_tdsp.value.duns_number,
        ...!show_historical_usage.value && usage_selected.value && { display_usage: usage_selected.value },
        ...show_historical_usage.value && { display_usage: historical_usage_values.value },
      },
      paramsSerializer: params => {
        return query_string.stringify(params)
      }
    });

    featured_plans.value = data.map(historical_usage_plan_map)
  }
  catch (err) {
    console.error(err);
  }
}

const plan_search = async function ({
                                      tdsp_duns,
                                      brand_id,
                                      term,
                                      percent_green,
                                      is_pre_pay,
                                      is_time_of_use,
                                      requires_auto_pay,
                                      display_usage
                                    }) {
  try {
    const { data } = await pricing_api.get(`${ process.env.VUE_APP_PLANS_SERVICE_URL }/api/plans/current`, {
      params: {
        group: 'default',
        tdsp_duns,
        ...brand_id && { brand_id },
        ...term && { term },
        ...percent_green && { percent_green },
        ...is_pre_pay && { is_pre_pay },
        ...is_time_of_use && { is_time_of_use },
        ...requires_auto_pay && { requires_auto_pay },
        ...display_usage && { display_usage },
        // current: true,
        // ended: false,
        // disabled: false
      },
      paramsSerializer: params => {
        return query_string.stringify(params)
      }
    });
    return data;
  }
  catch (err) {
    console.error(err);
  }
}

const sort_plans = function () {
  const { type, desc } = sort_option.value;
  const multiplier = desc ? -1 : 1;

  filtered_plans.value.sort((a, b) => {
    let value_a, value_b;
    if (type === 'price') {
      if (usage_type.value === 'historical' && a.total_cost && b.total_cost) {
        value_a = a.total_cost;
        value_b = b.total_cost;
      }
      else {
        value_a = a[`display_pricing_${ usage_selected.value }`].total;
        value_b = b[`display_pricing_${ usage_selected.value }`].total;
      }
    }
    else {
      value_a = a.product.term;
      value_b = b.product.term;
    }

    if (value_a > value_b)
      return -1 * multiplier
    else
      return 1 * multiplier;
  })
}

const get_plan_by_id = async function ({ plan_id }) {
  try {
    if (show_historical_usage.value) {
      const { data } = await pricing_api.get(`${ process.env.VUE_APP_PLANS_SERVICE_URL }/api/plans/${ plan_id }`, {
        params: {
          display_usage: historical_usage_values.value
        },
        paramsSerializer: params => {
          return query_string.stringify(params)
        }
      });
      return historical_usage_plan_map(data);
    }
    else {
      const { data } = await pricing_api.get(`${ process.env.VUE_APP_PLANS_SERVICE_URL }/api/plans/${ plan_id }`, {
        params: {
          display_usage: usage_selected.value
        },
        paramsSerializer: params => {
          return query_string.stringify(params)
        }
      });
      return plan_map(data);
    }
  }
  catch (err) {
    console.error(err);
  }
}

const calculate_plan_for_usage = async function ({ plan_id, usage }) {
  try {
    const { data } = await pricing_api.get(`${ process.env.VUE_APP_PLANS_SERVICE_URL }/api/plans/${ plan_id }/calculate/${ usage }`);
    return data;
  }
  catch (err) {
    console.error(err);
  }
}

const get_chart_data_for_plan = async function ({ plan_id, usage_start = 0, usage_end = 3000, step = 100 }) {
  try {
    const { data } = await pricing_api.get(`${ process.env.VUE_APP_PLANS_SERVICE_URL }/api/plans/${ plan_id }/chart/${ usage_start }/${ usage_end }/${ step }`)
    return data;
  }
  catch (err) {
    console.error(err);
  }
}


const { distinct } = Utilities();
const { available_brands_loading, available_brands } = Brands();
const { available_terms_loading, available_terms } = Terms();

const first_search = async function () {
  available_brands_loading.value = true;
  available_terms_loading.value = true;
  try {
    const all_plans = await plan_search({ tdsp_duns: selected_tdsp.value.duns_number });
    total_plans.value = all_plans.length;
    all_plans.map(x => x.product.brand)
    available_brands.value = [...new Map(all_plans.map(x => x.product.brand).map(item => [item['_id'], {
      ...item,
      selected: false
    }])).values()].sort((a, b) => a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1);
    available_terms.value = all_plans.map(x => x.product.term).sort((a, b) => a - b).filter(distinct).map(x => ({
      value: x,
      name: x === 1 ? 'Monthly / No-Contract' : `${ x } months`,
      selected: false
    }))
  }
  catch (err) {
    console.error(err);
  }
  finally {
    available_brands_loading.value = false;
    available_terms_loading.value = false;
  }
}

const last_changed_plan_prop = ref([]);
const combined_plan_change_property = computed(() => ({
  filters_prop: combined_filters.value,
  selected_tdsp_prop: selected_tdsp.value,
  usage_selected_prop: usage_selected.value,
  usage_type_prop: usage_type.value,
}));

watch(
  combined_plan_change_property,
  (newParams) => {
    debounce_plan_search();
  },
  { deep: true }
);

const debounce_plan_search = _debounce(async () => {
  await Promise.all([search_plans(), search_featured_plans()]);
  sort_plans()
  if(last_changed_plan_prop.value.includes('tdsp')) {
    first_search();
  }
  last_changed_plan_prop.value = [];
}, 300)

watch(selected_tdsp, async () => {
  if (selected_tdsp.value) {
    last_changed_plan_prop.value.push('tdsp')
  }
})

watch(filters, () => {
})

watch(sort_option, () => {
  sort_plans();
})

watch(usage_selected, async () => {
})

watch(usage_type, async () => {
})

export default () => {

  return {

    total_plans,

    filtered_plans,
    search_plans_loading,
    search_plans,

    featured_plans,
    search_featured_plans,

    plan_search,

    sort_plans,

    get_plan_by_id,
    calculate_plan_for_usage,
    get_chart_data_for_plan,
    plan_map,
    historical_usage_plan_map
  }

};
